import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Grid,
  GridColumn,
  GridRow,
  Suspense,
  Loader,
  ClientEngagementSelfServiceNoTalentMessage,
  useApi,
  Layout,
  Flashy,
  Button,
  useBreakpoint,
  Gutter,
} from '@axiom/ui';
import { AccountConst } from '@axiom/const';

import { PublicEngagementUrlUtil } from '../../utils/public-engagement-url-util';
import { useEngagementTalentData } from '../../hooks/useEngagementTalentData';
import { useEngagementInterviewTalentData } from '../../hooks/useEngagementInterviewTalentData';
import { PublicEngagementToLoginModal } from '../PublicEngagementToLoginModal/PublicEngagementToLoginModal';
import { AccountLegacyApi } from '../../api/account-legacy-api';
import { ClientEngagementNoTalentCard } from '../ClientEngagementNoTalentCard/ClientEngagementNoTalentCard';
import { EditEngagementButton } from '../EditEngagementButton/EditEngagementButton';

import { EngagementTalentList } from './EngagementTalentList';

export const EngagementTalent = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const [displayLoginModal, setDisplayLoginModal] = useState(true);
  const [displayMatchesLoginModal, setDisplayMatchesLoginModal] =
    useState(false);

  const {
    user,
    accountId,
    candidateOpp,
    candidateOppFromSlug,
    groupedCandidateOpps,
    submission,
    invalidSubmissionRequest,
    talentSlug,
    discount,
  } = useEngagementTalentData();

  const { interviewCount } = useEngagementInterviewTalentData();
  const [{ data: account } = { data: undefined }] = useApi(
    accountId && !!user
      ? AccountLegacyApi.getAccount(accountId, {
          expand: [AccountConst.Expansions.accountTeamUserInfo],
        })
      : undefined
  );

  const currentSubmissionCandidatesIsNotEmpty = groupedCandidateOpps.length > 0;
  const multipleRolesPresentInPositions = groupedCandidateOpps.length > 1;

  useEffect(() => {
    /**
     * If talentSlug is malformed removed it
     * and show the first talent in the list
     * */
    if (
      groupedCandidateOpps.length > 0 &&
      talentSlug &&
      !candidateOppFromSlug
    ) {
      navigate('..');
    }
  }, [groupedCandidateOpps, talentSlug]);

  useEffect(() => {
    if (invalidSubmissionRequest) {
      if (user?.id) {
        navigate(`/my-axiom/account/${accountId}`);
      } else {
        navigate(`/404`);
      }
    }
  }, [invalidSubmissionRequest]);

  return invalidSubmissionRequest || !submission ? null : (
    <div data-test="SUBMISSION">
      <Suspense fallback={<Loader type="bars" />}>
        {currentSubmissionCandidatesIsNotEmpty ? (
          <Grid>
            <GridRow name="talent-detail-row">
              <GridColumn largeScreenWidth={4} tabletWidth={4} mobileWidth={12}>
                {groupedCandidateOpps.map(group => (
                  <div key={group.title}>
                    <EngagementTalentList
                      hasMultipleRoles={multipleRolesPresentInPositions}
                      candidateOpportunities={group.data}
                      selectedCandidateId={candidateOpp?.candidate.id}
                      submission={submission}
                      title={group.title}
                      discount={discount}
                      onLoggedOutSubmissionClick={() => {
                        setDisplayMatchesLoginModal(true);
                      }}
                      account={account}
                      user={user}
                    />
                  </div>
                ))}
              </GridColumn>
              <GridColumn largeScreenWidth={8} tabletWidth={8} mobileWidth={12}>
                <Outlet />
              </GridColumn>
            </GridRow>
          </Grid>
        ) : (
          <div data-test="empty-talent-details">
            {submission.selfService ? (
              <ClientEngagementSelfServiceNoTalentMessage>
                <Layout
                  horizontalGutter="16px"
                  position="middle"
                  stackableOn="mobile"
                >
                  {submission.editable ? (
                    <>
                      <EditEngagementButton
                        account={account}
                        fluid={!!isMobile}
                        label="Edit legal needs"
                        name="no-matches-edit-engagement-button"
                        onLoggedOutSubmissionClick={() => {
                          setDisplayMatchesLoginModal(true);
                        }}
                        pattern="primary"
                        user={user}
                        variation="filled"
                      />
                      <Gutter only="mobile" bottom="16px" />
                      <Flashy name="no-matches-flashy" bold>
                        OR
                      </Flashy>
                      <Gutter only="mobile" bottom="16px" />
                    </>
                  ) : null}
                  <Button
                    fluid={!!isMobile}
                    name="no-matches-browse-talent-button"
                    onClick={() => {
                      if (user) {
                        navigate(`/browse-talent`);
                      } else {
                        setDisplayMatchesLoginModal(true);
                      }
                    }}
                    variation="outline"
                  >
                    Browse talent
                  </Button>
                </Layout>
              </ClientEngagementSelfServiceNoTalentMessage>
            ) : (
              <ClientEngagementNoTalentCard
                onLoggedOutSubmissionClick={() => {
                  setDisplayMatchesLoginModal(true);
                }}
                account={account}
                user={user}
              />
            )}
            {!user && interviewCount > 0 && displayLoginModal ? (
              <PublicEngagementToLoginModal
                pathTo={`${window.location.origin}${PublicEngagementUrlUtil(
                  location.pathname
                ).fromPublicMatchesToInterviews()}`}
                onClose={() => {
                  setDisplayLoginModal(false);
                }}
                bodyContent="It looks like all of the talent on this submission are currently in the interviewing stage, please sign in to take next steps."
                bodyFooter="Once signed in you will be redirected to your Axiom client account with full access to this talent submission and all talent on the Interviews tab."
              />
            ) : null}
          </div>
        )}
      </Suspense>
      {!user && displayMatchesLoginModal ? (
        <div data-test="MATCHES_LOGIN_CONTAINER">
          <PublicEngagementToLoginModal
            onClose={() => {
              setDisplayMatchesLoginModal(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
